import React, { useState } from 'react';
import {
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  TextArea,
  Button,
  useToast,
} from '@razorpay/blade/components';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import employeeBankAccountApis, { GET_BANK_ACCOUNT_REQUESTS } from 'api/employeeBankAccount';
import { useMutation } from 'react-query';
import { useQueryClient } from 'react-query';
import { overlayLayers } from 'constants/overlayLayers.generated';
import { useWorkflowDetailsView } from '../../useWorkflowDetailsView';

export const RejectModal = ({
  isOpen,
  onDismiss,
  workflowRequestId,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  workflowRequestId: string | null;
}) => {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const isRxCurrentAccountMerchant = !!useSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const toast = useToast();
  const { closeDetailsView } = useWorkflowDetailsView();

  const handleDismiss = () => {
    setComment('');
    onDismiss();
  };

  const { mutate: rejectRequests } = useMutation({
    mutationFn: employeeBankAccountApis.bankAccountRequestReject,
    onSuccess: ({ data: { successful, total, failed } }) => {
      if (total && successful === total) {
        queryClient.invalidateQueries({
          queryKey: [GET_BANK_ACCOUNT_REQUESTS],
        });
        toast.show({
          content: 'Request rejected successfully',
          color: 'positive',
        });
        handleDismiss();
        closeDetailsView();
      }
      if (failed) {
        toast.show({
          content: 'Failed to reject request, please try later',
          color: 'negative',
        });
      }
    },
    onError: () => {
      toast.show({
        content: 'Failed to reject request, please try later',
        color: 'negative',
      });
    },
  });

  const handleSubmit = () => {
    if (workflowRequestId) {
      rejectRequests({ reason: comment, change_request_ids: [workflowRequestId] });
    }
  };

  return (
    <Modal
      zIndex={overlayLayers.modal}
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size="small"
      accessibilityLabel="bank account request approval modal">
      <ModalHeader title="Reject Request" />
      <ModalBody>
        <Box>
          <Text size="medium" weight="semibold" color="surface.text.gray.normal">
            Are you sure you want to reject this request?
          </Text>
        </Box>
        <Box marginTop="spacing.6">
          <TextArea
            value={comment}
            onChange={({ value }) => {
              setComment(value ?? '');
            }}
            label="Comment "
            isRequired={isRxCurrentAccountMerchant ? false : true}
            necessityIndicator={isRxCurrentAccountMerchant ? 'optional' : 'required'}
            placeholder="Enter a reason to reject request"
            marginTop="spacing.3"
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.5" width="100%">
          <Button variant="tertiary" onClick={handleDismiss}>
            Cancel
          </Button>
          <Button
            isDisabled={isRxCurrentAccountMerchant ? false : !comment.trim()}
            variant="primary"
            color="negative"
            onClick={handleSubmit}>
            Reject
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
